import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { injectIntl, intlShape } from '../../util/reactIntl';
import { Page, LayoutSingleColumn } from '../../components';
import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';
import { withViewport } from '../../util/uiHelpers';
import { useConfiguration } from '../../context/configurationContext';

import css from './LandingPage.module.css';

//Static assets
import facebookImage from '../../assets/biketribe-facebook-sharing-1200x630.jpg';
import twitterImage from '../../assets/biketribe-twitter-sharing-600x314.jpg';
import SectionHero from './SectionHero/SectionHero';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import ExploreTalent from './Sections/ExploreTalent';
import ExploreServices from './Sections/ExploreServices';
import ExploreSchools from './Sections/ExploreSchools';
import SectionExportSports from './Sections/ExploreSports';
import routeConfiguration from '../../routing/routeConfiguration';
import { createResourceLocatorString } from '../../util/routes';
import { UserType } from '../../util/enums';
import { filterListingsByUserType } from '../../util/listingDataExtraction';
import ExploreBrands from './Sections/ExploreBrands';

export const LandingPageComponent = props => {
  const { intl, scrollingDisabled, history, viewport, isAuthenticated, listings } = props;

  const isMobileLayout = viewport && viewport.width > 0 && viewport.width < 768;

  const config = useConfiguration();

  const marketplaceName = config.marketplaceName;

  const schemaTitle = intl.formatMessage({ id: 'LandingPage.schemaTitle' }, { marketplaceName });
  const schemaDescription = intl.formatMessage({
    id: 'LandingPage.schemaDescription',
  });
  const schemaImage = `${config.marketplaceRootURL}${facebookImage}`;

  const useHistoryPush = (page, params = {}, search = {}) => {
    history.push(createResourceLocatorString(page, routeConfiguration(), params, search));
  };

  const schoolListings = filterListingsByUserType(listings, UserType.SCHOOL);
  const talentListings = filterListingsByUserType(listings, UserType.TALENT);
  const brandListings = filterListingsByUserType(listings, UserType.BRAND);

  return (
    <Page
      className={css.root}
      scrollingDisabled={scrollingDisabled}
      contentType="website"
      description={schemaDescription}
      title={schemaTitle}
      facebookImages={[{ url: facebookImage, width: 1200, height: 630 }]}
      twitterImages={[
        {
          url: `${config.marketplaceRootURL}${twitterImage}`,
          width: 600,
          height: 314,
        },
      ]}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        description: schemaDescription,
        name: schemaTitle,
        image: [schemaImage],
      }}
    >
      <LayoutSingleColumn
        className={css.pageRoot}
        topbar={<TopbarContainer viewport={viewport} />}
        footer={<FooterContainer />}
      >
        <div className={css.pageContent}>
          <SectionHero
            listings={listings}
            intl={intl}
            useHistoryPush={useHistoryPush}
            isAuthenticated={isAuthenticated}
            isMobileLayout={isMobileLayout}
          />
          <ExploreServices
            isMobileLayout={isMobileLayout}
            intl={intl}
            viewport={viewport}
            useHistoryPush={useHistoryPush}
          />

          <ExploreTalent
            listings={talentListings}
            intl={intl}
            isMobileLayout={isMobileLayout}
            isLandingListingCard={true}
            useHistoryPush={useHistoryPush}
            viewport={viewport}
          />
          <SectionExportSports
            intl={intl}
            isMobileLayout={isMobileLayout}
            useHistoryPush={useHistoryPush}
            viewport={viewport}
          />
          <ExploreSchools
            listings={schoolListings}
            intl={intl}
            viewport={viewport}
            useHistoryPush={useHistoryPush}
          />

          <ExploreBrands
            listings={brandListings}
            isBrand
            intl={intl}
            isMobileLayout={isMobileLayout}
            isLandingListingCard={true}
            useHistoryPush={useHistoryPush}
            viewport={viewport}
            isBrandListings={true}
          />
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const { bool } = PropTypes;

LandingPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const { isAuthenticated } = state.auth;
  const { currentPageResultIds } = state.LandingPage;
  const listings = getListingsById(state, currentPageResultIds);
  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    isAuthenticated,
    listings,
  };
};

const LandingPage = compose(
  withRouter,
  connect(mapStateToProps, null),
  withViewport,
  injectIntl
)(LandingPageComponent);

export default LandingPage;
